// AM-030: Zustand slice managing the in-editor state of a routing
// revision's operation dependency graph (before/alongside orval-generated
// React Query hooks handle the actual API calls).
import { create } from "zustand";
import {
  CreateDependencyEdgeInput,
  OperationJoinType,
  RoutingOperationDependencyEdge,
  RoutingOperationSummary,
  wouldCreateCycle,
} from "./am030-types";

interface Am030GraphState {
  operations: RoutingOperationSummary[];
  edges: RoutingOperationDependencyEdge[];
  pendingEdgeError: string | null;

  setOperations: (operations: RoutingOperationSummary[]) => void;
  setEdges: (edges: RoutingOperationDependencyEdge[]) => void;

  /** Returns null if the edge is valid to attempt, or an error message otherwise. */
  validateCandidateEdge: (input: CreateDependencyEdgeInput) => string | null;

  addEdgeOptimistic: (edge: RoutingOperationDependencyEdge) => void;
  removeEdge: (edgeId: string) => void;

  setOperationJoinType: (operationId: string, joinType: OperationJoinType) => void;
}

export const useAm030GraphStore = create<Am030GraphState>((set, get) => ({
  operations: [],
  edges: [],
  pendingEdgeError: null,

  setOperations: (operations) => set({ operations }),
  setEdges: (edges) => set({ edges }),

  validateCandidateEdge: (input) => {
    const { upstreamOperation, downstreamOperation } = input;
    if (upstreamOperation === downstreamOperation) {
      return "یک عملیات نمی‌تواند پیش‌نیاز خودش باشد.";
    }
    const { edges } = get();
    const duplicate = edges.some((e: any) => {
      const up = e.upstream_operation ?? e.upstreamOperation;
      const down = e.downstream_operation ?? e.downstreamOperation;
      return up === upstreamOperation && down === downstreamOperation;
    });
    if (duplicate) {
      return "این وابستگی از قبل وجود دارد.";
    }
    if (wouldCreateCycle(edges, upstreamOperation, downstreamOperation)) {
      return "این وابستگی باعث ایجاد یک دور (cycle) می‌شود.";
    }
    return null;
  },

  addEdgeOptimistic: (edge) => set((state) => ({ edges: [...state.edges, edge] })),

  removeEdge: (edgeId) =>
    set((state) => ({ edges: state.edges.filter((e) => e.id !== edgeId) })),

  setOperationJoinType: (operationId, joinType) =>
    set((state) => ({
      operations: state.operations.map((op) =>
        op.id === operationId ? { ...op, upstreamJoinType: joinType } : op
      ),
    })),
}));
